import { type DatabaseSubscription } from "@/hooks/useSubscriptions";
import { deriveNextPaymentDate, daysUntil } from "@/lib/billingSchedule";
import { type PaymentHistory } from "@/repositories/interfaces";

/**
 * Calculates the number of days until a subscription's next payment
 */
export const getDaysUntilPayment = (nextPaymentDate: string): number => {
  return Math.ceil(
    (new Date(nextPaymentDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
  );
};

/**
 * Determines if a subscription needs reconciliation (pending action)
 * A subscription is pending if it's active and due within 3 days (including overdue)
 */
export const isPendingReconciliation = (
  subscription: DatabaseSubscription,
  payments: PaymentHistory[] = [],
): boolean => {
  if (subscription.status !== "active") {
    return false;
  }

  const nextPaymentDate = deriveNextPaymentDate({
    billingCycle: subscription.billing_cycle,
    storedNextPaymentDate: subscription.next_payment_date,
    paidDates: payments
      .filter((payment) => payment.subscription_id === subscription.id)
      .filter((payment) => payment.status === "paid")
      .map((payment) => payment.payment_date),
  });
  if (!nextPaymentDate) {
    return false;
  }

  // Check if subscription is currently snoozed
  if (subscription.reconciliation_snoozed_until) {
    const snoozeUntil = new Date(subscription.reconciliation_snoozed_until);
    const now = new Date();

    // If still snoozed, don't show as pending
    if (snoozeUntil > now) {
      return false;
    }
  }

  return daysUntil(nextPaymentDate) <= 3;
};

/**
 * Gets all subscriptions that need reconciliation
 */
export const getPendingSubscriptions = (
  subscriptions: DatabaseSubscription[],
  payments: PaymentHistory[] = [],
): DatabaseSubscription[] => {
  return subscriptions.filter((subscription) =>
    isPendingReconciliation(subscription, payments),
  );
};

/**
 * Counts the number of pending subscriptions
 */
export const getPendingCount = (
  subscriptions: DatabaseSubscription[],
  payments: PaymentHistory[] = [],
): number => {
  return getPendingSubscriptions(subscriptions, payments).length;
};

/**
 * Determines if a subscription is currently snoozed
 */
export const isSnoozed = (subscription: DatabaseSubscription): boolean => {
  if (!subscription.reconciliation_snoozed_until) {
    return false;
  }

  const snoozeUntil = new Date(subscription.reconciliation_snoozed_until);
  const now = new Date();

  return snoozeUntil > now;
};

/**
 * Gets all subscriptions that are currently snoozed
 */
export const getSnoozedSubscriptions = (
  subscriptions: DatabaseSubscription[],
): DatabaseSubscription[] => {
  return subscriptions.filter(isSnoozed);
};

/**
 * Counts the number of snoozed subscriptions
 */
export const getSnoozedCount = (
  subscriptions: DatabaseSubscription[],
): number => {
  return getSnoozedSubscriptions(subscriptions).length;
};

/**
 * Calculate snooze until date based on duration option
 */
export const calculateSnoozeUntil = (duration: string): Date => {
  const now = new Date();

  switch (duration) {
    case "end_of_month": {
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      lastDay.setHours(23, 59, 59, 999);
      return lastDay;
    }
    case "1_day":
      return new Date(now.getTime() + 24 * 60 * 60 * 1000);
    case "3_days":
      return new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    case "1_week":
      return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    case "2_weeks":
      return new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    case "1_month":
      return new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
    default:
      return new Date(now.getTime() + 24 * 60 * 60 * 1000);
  }
};

/**
 * Snooze duration option type
 */
export interface SnoozeDuration {
  label: string;
  value: string;
  description?: string;
}

export const SNOOZE_DURATION_OPTIONS: SnoozeDuration[] = [
  {
    label: "Until end of month",
    value: "end_of_month",
    description: "Reappears on the 1st of next month",
  },
  {
    label: "1 day",
    value: "1_day",
    description: "Reappears tomorrow",
  },
  {
    label: "3 days",
    value: "3_days",
  },
  {
    label: "1 week",
    value: "1_week",
  },
  {
    label: "2 weeks",
    value: "2_weeks",
  },
  {
    label: "1 month",
    value: "1_month",
  },
];
